import React, { useEffect, useState, useCallback } from 'react';
import { Table, Tag, Button } from 'antd';
import { ReloadOutlined, SendOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { dashboardApi, type PostEvaluationItem } from '@/api/dashboardApi';
import { parseDateLocal } from '@/utils/dateFormatters';
import PageHeader from '@/components/ui/PageHeader';
import EmptyState from '@/components/ui/EmptyState';

const outcomeColors: Record<string, string> = {
  WON: 'success',
  LOST: 'error',
  NO_DECISION: 'default',
  PENDING: 'warning',
};

const outcomeLabels: Record<string, string> = {
  WON: 'Won',
  LOST: 'Lost',
  NO_DECISION: 'No Decision',
  PENDING: 'Pending',
};

const formatDate = (value?: string) => {
  if (!value) return '—';
  // Date-only strings ("YYYY-MM-DD") must be parsed in local time, not UTC.
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return parseDateLocal(value).format('MMM D, YYYY');
  }
  return new Date(value).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const TAB_KEYS = ['ALL', 'WON', 'LOST', 'PENDING', 'NO_DECISION'] as const;
type TabKey = (typeof TAB_KEYS)[number];

const tabLabels: Record<TabKey, string> = {
  ALL: 'All',
  WON: 'Won',
  LOST: 'Lost',
  PENDING: 'Pending',
  NO_DECISION: 'No Decision',
};

const PostSubmissionPage: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<PostEvaluationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabKey>('ALL');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await dashboardApi.getPostEvaluation();
      setData(result);
    } catch (err) {
      console.error('Failed to fetch post-submission data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered =
    activeTab === 'ALL' ? data : data.filter((item) => item.outcome === activeTab);

  const formatDateTime = (value?: string) => {
    if (!value) return '—';
    return new Date(value).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'medium' });
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      sorter: (a: PostEvaluationItem, b: PostEvaluationItem) =>
        (a.title || '').localeCompare(b.title || ''),
      render: (text: string, record: PostEvaluationItem) => (
        <a onClick={() => navigate(`/opportunities/${record.id}`)}>{text}</a>
      ),
    },
    {
      title: 'Agency',
      dataIndex: 'agencyName',
      key: 'agencyName',
      sorter: (a: PostEvaluationItem, b: PostEvaluationItem) =>
        (a.agencyName || '').localeCompare(b.agencyName || ''),
      render: (v: string) => v || '—',
    },
    {
      title: 'Outcome',
      dataIndex: 'outcome',
      key: 'outcome',
      sorter: (a: PostEvaluationItem, b: PostEvaluationItem) =>
        (a.outcome || '').localeCompare(b.outcome || ''),
      render: (outcome: string) => (
        <Tag color={outcomeColors[outcome] || 'default'}>
          {outcomeLabels[outcome] || outcome}
        </Tag>
      ),
    },
    {
      title: 'Conducted By',
      dataIndex: 'conductedBy',
      key: 'conductedBy',
      sorter: (a: PostEvaluationItem, b: PostEvaluationItem) =>
        (a.conductedBy || '').localeCompare(b.conductedBy || ''),
      render: (v: string) => v || '—',
    },
    {
      title: 'Last Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      defaultSortOrder: 'descend' as const,
      sorter: (a: PostEvaluationItem, b: PostEvaluationItem) => {
        const aT = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const bT = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        return aT - bT;
      },
      render: (v: string) => formatDateTime(v),
    },
  ];

  const tabItems = TAB_KEYS.map((key) => {
    const count = key === 'ALL' ? data.length : data.filter((d) => d.outcome === key).length;
    return { key, count };
  });

  return (
    <div>
      <PageHeader
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <SendOutlined style={{ fontSize: 20, color: 'var(--color-primary)' }} />
            Post-Submission Opportunities
          </span>
        }
        subtitle={`${data.length} total · ${filtered.length} shown`}
        actions={
          <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
            Refresh
          </Button>
        }
      />

      {/* Outcome filter chips */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {tabItems.map(({ key, count }) => (
          <button
            key={key}
            type="button"
            className="ui-chip ui-focusable"
            data-active={activeTab === key}
            onClick={() => setActiveTab(key as TabKey)}
          >
            {tabLabels[key]}
            {count > 0 && <span className="ui-chip__count">{count}</span>}
          </button>
        ))}
      </div>

      <div className="ui-surface ui-animate-in" style={{ overflow: 'hidden' }}>
        <Table
          dataSource={filtered}
          rowKey="id"
          columns={columns}
          loading={loading}
          size="middle"
          scroll={{ x: 900 }}
          pagination={{ pageSize: 20, showSizeChanger: false, hideOnSinglePage: true }}
          onRow={(record) => ({
            onClick: () => navigate(`/opportunities/${record.id}`),
            style: { cursor: 'pointer' },
          })}
          locale={{
            emptyText: loading ? (
              <span />
            ) : (
              <EmptyState
                icon={<SendOutlined />}
                title={
                  activeTab === 'ALL'
                    ? 'No post-submission opportunities yet'
                    : `No ${tabLabels[activeTab].toLowerCase()} opportunities`
                }
                description={
                  activeTab === 'ALL'
                    ? 'Submitted opportunities and their outcomes will appear here.'
                    : 'Try switching to another outcome filter.'
                }
              />
            ),
          }}
        />
      </div>
    </div>
  );
};

export default PostSubmissionPage;
